// ============================================================
// YOM Connect (docs/integrations/connect.md): the /app/connect consent page and
// the connected-integrations list in Settings > API keys. Injected into v2Api.
// ============================================================

import { v2Api } from './v2Api'
import { unwrap, type V2Envelope } from './v2Mappers'

export interface ConnectGrant {
    type: 'pk' | 'sk'
    scopes: string[]
    description: string
}

export interface ConnectRequestInfo {
    platform: { id: string; name: string }
    store: string
    grants: ConnectGrant[]
}

export interface ConnectInstall {
    id: string
    platform: string
    store: string
    sub_tenant: string | null
    key_ids: string[]
    created_by: string
    created_at: string
    status: 'active' | 'revoked'
    revoked_at: string | null
    revoked_reason: string | null
}

export interface AuthorizeConnectArgs {
    projectId: string
    platform: string
    store: string
    return: string
    state: string
    code_challenge: string
    code_challenge_method: 'S256'
    sub_tenant: string | null
}

const enc = encodeURIComponent

export const connectApi = v2Api.injectEndpoints({
    endpoints: builder => ({
        getConnectRequest: builder.query<
            ConnectRequestInfo,
            { platform: string; store: string; returnUrl: string }
        >({
            query: a =>
                `/connect/request?${new URLSearchParams({ platform: a.platform, store: a.store, return: a.returnUrl })}`,
            transformResponse: (res: V2Envelope<ConnectRequestInfo>) => unwrap(res),
        }),
        authorizeConnect: builder.mutation<{ redirect_url: string }, AuthorizeConnectArgs>({
            query: ({ projectId, ...body }) => ({
                url: `/projects/${enc(projectId)}/connect/authorize`,
                method: 'POST',
                body,
            }),
            transformResponse: (res: V2Envelope<{ redirect_url: string }>) => unwrap(res),
        }),
        listConnectInstalls: builder.query<ConnectInstall[], string>({
            query: projectId => `/projects/${enc(projectId)}/connect/installs`,
            transformResponse: (res: V2Envelope<ConnectInstall[]>) => unwrap(res),
            providesTags: ['ConnectInstalls'],
        }),
        revokeConnectInstall: builder.mutation<unknown, { projectId: string; id: string }>({
            query: a => ({
                url: `/projects/${enc(a.projectId)}/connect/installs/${enc(a.id)}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['ConnectInstalls', 'ProjectKeys'],
        }),
    }),
})

export const {
    useGetConnectRequestQuery,
    useAuthorizeConnectMutation,
    useListConnectInstallsQuery,
    useRevokeConnectInstallMutation,
} = connectApi
